(() => {
  const SELECTOR = "[data-cart-social-proof]";

  class UFCartSocialProof {
    constructor(root) {
      if (!root || root.dataset.proofInitialized === "true") return;

      this.root = root;
      this.root.dataset.proofInitialized = "true";

      this.slides = Array.from(
        this.root.querySelectorAll("[data-proof-slide]")
      );

      this.viewport = this.root.querySelector("[data-proof-viewport]");
      this.previousButton = this.root.querySelector("[data-proof-prev]");
      this.nextButton = this.root.querySelector("[data-proof-next]");
      this.currentCounter = this.root.querySelector("[data-proof-current]");
      this.progress = this.root.querySelector("[data-proof-progress]");

      this.index = 0;

      this.autoplayEnabled =
        this.root.dataset.autoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.autoplayDelay =
        Number.parseInt(this.root.dataset.autoplayDelay, 10) || 6000;

      this.timer = null;

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;

      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);

      if (!this.slides.length) return;

      this.bindEvents();
      this.showSlide(0, false);

      if (this.slides.length > 1) {
        this.startAutoplay();
      }
    }


    bindEvents() {
      this.previousButton?.addEventListener("click", () => {
        this.previous();
        this.restartAutoplay();
      });

      this.nextButton?.addEventListener("click", () => {
        this.next();
        this.restartAutoplay();
      });


      this.root.addEventListener("mouseenter", () => {
        this.stopAutoplay();
      });

      this.root.addEventListener("mouseleave", () => {
        this.startAutoplay();
      });


      this.root.addEventListener("focusin", () => {
        this.stopAutoplay();
      });

      this.root.addEventListener("focusout", (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.startAutoplay();
        }
      });


      if (this.viewport) {
        this.viewport.addEventListener(
          "touchstart",
          (event) => {
            const touch = event.changedTouches[0];

            if (!touch) return;

            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;

            this.stopAutoplay();
          },
          { passive: true }
        );


        this.viewport.addEventListener(
          "touchend",
          (event) => {
            const touch = event.changedTouches[0];

            if (!touch) return;

            this.touchEndX = touch.clientX;
            this.touchEndY = touch.clientY;

            this.handleSwipe();
            this.startAutoplay();
          },
          { passive: true }
        );
      }


      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }


    showSlide(index, animate = true) {
      if (!this.slides.length) return;

      if (index < 0) {
        index = this.slides.length - 1;
      }

      if (index >= this.slides.length) {
        index = 0;
      }

      this.index = index;


      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;

        slide.classList.toggle("is-active", active);
        slide.setAttribute(
          "aria-hidden",
          active ? "false" : "true"
        );

        if (active && animate) {
          slide.style.animation = "none";

          void slide.offsetWidth;

          slide.style.animation = "";
        }
      });


      this.updateCounter();
      this.updateProgress();
    }


    next() {
      this.showSlide(this.index + 1);
    }


    previous() {
      this.showSlide(this.index - 1);
    }


    updateCounter() {
      if (!this.currentCounter) return;

      this.currentCounter.textContent = String(
        this.index + 1
      ).padStart(2, "0");
    }


    updateProgress() {
      if (!this.progress) return;

      const percentage =
        ((this.index + 1) / this.slides.length) * 100;

      this.progress.style.width = `${percentage}%`;
    }


    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.slides.length <= 1 ||
        this.timer ||
        document.hidden
      ) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.next();
      }, this.autoplayDelay);
    }


    stopAutoplay() {
      if (!this.timer) return;

      window.clearInterval(this.timer);
      this.timer = null;
    }


    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }


    handleSwipe() {
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;

      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);

      const minimumSwipe = 45;

      if (
        horizontalDistance < minimumSwipe ||
        horizontalDistance <= verticalDistance
      ) {
        return;
      }

      if (deltaX < 0) {
        this.next();
      } else {
        this.previous();
      }

      this.restartAutoplay();
    }


    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }
  }


  function initialize(scope = document) {
    const roots = [];

    if (scope.matches?.(SELECTOR)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(SELECTOR));
    }

    roots.forEach((root) => {
      new UFCartSocialProof(root);
    });
  }


  function observeCartUpdates() {
    const observer = new MutationObserver((mutations) => {
      let shouldInitialize = false;

      for (const mutation of mutations) {
        if (!mutation.addedNodes.length) continue;

        shouldInitialize = true;
        break;
      }

      if (shouldInitialize) {
        initialize(document);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }


  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initialize();
      observeCartUpdates();
    });
  } else {
    initialize();
    observeCartUpdates();
  }


  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();